/**
 * Color Blending Utilities for Additive Highlighting
 * 
 * Implements RGB additive color mixing for word-level highlights
 */

/**
 * Convert hex color to RGB components
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  // Guard against undefined/null input - use gray as fallback
  if (!hex) {
    hex = '#CCCCCC';
  }
  
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  };
}

/**
 * Convert RGB components to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    const clamped = Math.min(255, Math.max(0, Math.round(n)));
    return clamped.toString(16).padStart(2, '0');
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Blend multiple colors using additive RGB mixing
 * For highlighting: simulates multiple highlighter layers
 * 
 * Note: Pure additive mixing can get very bright. We use a weighted
 * average approach that brightens but doesn't completely wash out.
 */
export function blendColors(colors: string[]): string {
  if (colors.length === 0) return '#FFFFFF';
  if (colors.length === 1) return colors[0];
  
  // Convert all colors to RGB
  const rgbs = colors.map(hexToRgb);
  
  // Additive mixing with brightness limiting
  // We average the colors but boost the brightness
  let r = 0, g = 0, b = 0;
  
  for (const rgb of rgbs) {
    r += rgb.r;
    g += rgb.g;
    b += rgb.b;
  }
  
  // Average to prevent overflow, but keep it bright
  const count = rgbs.length;
  const blend = {
    r: Math.min(255, (r / count) * 1.5), // 1.5x boost for visibility
    g: Math.min(255, (g / count) * 1.5),
    b: Math.min(255, (b / count) * 1.5),
  };
  
  return rgbToHex(blend.r, blend.g, blend.b);
}

/**
 * Get a lighter/brighter version of a color for better readability
 */
export function lightenColor(hex: string, amount: number = 0.3): string {
  const rgb = hexToRgb(hex);
  
  return rgbToHex(
    rgb.r + (255 - rgb.r) * amount,
    rgb.g + (255 - rgb.g) * amount,
    rgb.b + (255 - rgb.b) * amount
  );
}



























